import { get, writable } from 'svelte/store';

import { apiClient, ApiError } from '$lib/api/client';
import { guardToken } from '$lib/turnstile';
import { queryFilters } from '$lib/stores/selection';
import { logger } from '$lib';

/**
 * A customer's own list of companies, and the selection that leaves it out.
 *
 * # Why the code lives here and not only in the filters
 *
 * The filters carry `exclude_list_code` because that is what the search reads.
 * But the customer needs more than the code: whether the matching has finished,
 * how many of their rows we recognised, and a way to take the list back. So the
 * list is a small store of its own, and the code is copied into the filters
 * whenever the exclusion is on.
 *
 * # Why it is kept in local storage
 *
 * There are no accounts. A customer who reloads the page, or comes back after
 * lunch, would otherwise have to upload the file again — and the list is still
 * sitting on the server, unreachable because the only thing that named it was
 * a variable in a closed tab.
 */

/** Where a list is in its life; the same words the service uses. */
export type ListStatus = 'pending' | 'matching' | 'ready' | 'failed';

export interface CompanyList {
  code: string;
  status: ListStatus;
  name: string;
  rows: number;
  matched: number;
  failure?: string;
}

/** One line of an uploaded list, as it was given and as it resolved. */
export interface ListRow {
  line: number;
  name: string;
  uid?: string;
  /** The website as the customer wrote it, which is also what it matched on. */
  domain?: string;
  zip?: string;
  city?: string;
  street?: string;
  company_id?: string;
  matched_by?: 'id' | 'uid' | 'domain' | 'name_address' | 'name';
  score?: number;
}

/** The list the customer is working with, if any. */
export const customerList = writable<CompanyList | null>(null);

/**
 * Whether the list is being subtracted.
 *
 * Separate from holding a list at all, because looking at what matched and
 * removing it from the selection are two different decisions — and a customer
 * who wants to see the overlap before acting on it should be able to.
 */
export const listExcluded = writable(false);

const storageKey = 'ainova.customerList';

/**
 * Whether there is a browser to store anything in.
 *
 * Asked directly rather than through `$app/environment`, because this module is
 * imported by the search paths, which are unit-tested outside SvelteKit — and
 * the alias does not resolve there.
 */
function inBrowser(): boolean {
  return typeof window !== 'undefined';
}

/** Read the list back after a reload. */
export function restoreCustomerList(): void {
  if (!inBrowser()) return;

  const raw = window.localStorage.getItem(storageKey);
  if (!raw) return;

  try {
    const saved = JSON.parse(raw) as { list: CompanyList; excluded: boolean };
    if (!saved?.list?.code) return;

    customerList.set(saved.list);
    listExcluded.set(saved.excluded);
    applyExclusion();

    // The server may have expired it in the meantime, and the app must not
    // keep a code that no longer names anything: every search carrying it
    // would be refused.
    void refreshList().catch(() => forgetList());
  } catch {
    window.localStorage.removeItem(storageKey);
  }
}

/** Remember the list across reloads. */
function remember(): void {
  if (!inBrowser()) return;

  const list = get(customerList);
  if (!list) {
    window.localStorage.removeItem(storageKey);
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify({ list, excluded: get(listExcluded) }));
}

/**
 * Put the code where the search reads it, or take it away.
 *
 * A list that is still matching is subtracted with what it has so far. The
 * alternative — withholding the exclusion until every row has a verdict — shows
 * the customer companies they own and then quietly removes them, which reads as
 * the results changing on their own.
 */
export function applyExclusion(): void {
  const list = get(customerList);
  const code = get(listExcluded) && list ? list.code : '';

  queryFilters.update((filters) => ({ ...filters, exclude_list_code: code }));
  remember();
}

/** Upload a file and start matching it. */
export async function uploadList(file: File): Promise<CompanyList> {
  const turnstileToken = await guardToken();

  const form = new FormData();
  form.append('file', file);
  form.append('name', file.name);
  // In the form as well as the header, because a guard that reads only one of
  // them should still find it. The header is what the service checks first.
  if (turnstileToken) form.append('turnstile_token', turnstileToken);

  // The header, not just the field: finding a token in a multipart form means
  // parsing the whole upload before deciding whether to accept it, and the
  // browser puts the file before the field. Sent this way the guard reads one
  // header and never touches the body.
  const response = await apiClient.upload<CompanyList>('/deep-iq/lists', form, {
    headers: turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : {}
  });

  customerList.set(response.data);
  listExcluded.set(true);
  applyExclusion();

  return response.data;
}

/** Adopt a list somebody already has the code for. */
export async function adoptList(code: string): Promise<CompanyList | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;

  try {
    const response = await apiClient.get<CompanyList>(
      `/deep-iq/lists/${encodeURIComponent(trimmed)}`
    );

    customerList.set(response.data);
    listExcluded.set(true);
    applyExclusion();

    return response.data;
  } catch {
    return null;
  }
}

/** Ask what the list says about itself now. */
export async function refreshList(): Promise<CompanyList | null> {
  const list = get(customerList);
  if (!list) return null;

  const response = await apiClient.get<CompanyList>(
    `/deep-iq/lists/${encodeURIComponent(list.code)}`
  );

  customerList.set(response.data);
  remember();
  return response.data;
}

/** The rows, matched and unmatched, for the customer to look through. */
export async function fetchListRows(): Promise<ListRow[]> {
  const list = get(customerList);
  if (!list) return [];

  const response = await apiClient.get<{ rows: ListRow[] }>(
    `/deep-iq/lists/${encodeURIComponent(list.code)}/rows`
  );
  return response.data.rows ?? [];
}

/** Delete the list from the server, at the customer's word. */
export async function deleteList(): Promise<void> {
  const list = get(customerList);
  if (!list) return;

  try {
    await apiClient.delete(`/deep-iq/lists/${encodeURIComponent(list.code)}`);
  } finally {
    forgetList();
  }
}

/**
 * Throw the list away, here and there, without waiting for the server.
 *
 * # Why starting a new selection takes the list with it
 *
 * It used to survive: the list was work to produce, it has its own code, and a
 * fresh selection only switched the subtraction off. That was right while a
 * list was an optional last step — something a customer added at the end to
 * avoid buying rows twice.
 *
 * The list door changed what a list is. It can now be the thing the whole
 * selection was derived from, so a selection started afresh while the file that
 * shaped the previous one is still held is a selection with an invisible
 * history: the filters it produced have just been cleared, and the file that
 * produced them has not.
 *
 * And it is the customer's own customer base. Keeping it on the server after
 * they have said "start again" is exactly the liability the retention window
 * exists to limit — see internal/domain/companylist.go.
 *
 * # Why the local half does not wait for the remote half
 *
 * deleteList clears the stores in a `finally`, so a slow or failed request
 * leaves the list on screen after the customer asked for it to go. Here the
 * stores are cleared first and the delete follows best-effort: the worst case
 * is a list nobody can reach any more expiring on its own within the day, which
 * is what retention is for.
 */
export function discardList(): void {
  const list = get(customerList);

  // # Nothing held is not the same as something to throw away
  //
  // This returns before touching anything, and the reason is the order the
  // layout mounts in: it calls resetSelection first — to clear a selection
  // carried back from checkout — and restoreCustomerList second, to put the
  // stored list back. At the moment of the reset the list is in local storage
  // and not yet in the store.
  //
  // Clearing here would therefore delete the code the customer is a line away
  // from returning to. That is what forgetList did, through applyExclusion and
  // remember, and it is why a list never in fact survived a reload despite the
  // layout being written to restore one.
  if (!list) return;

  // Read the code before forgetting it, or there is nothing left to delete.
  const code = list.code;
  forgetList();

  void apiClient.delete(`/deep-iq/lists/${encodeURIComponent(code)}`).catch((error) => {
    // Nothing to tell the customer: from where they are standing the list is
    // already gone, and it will expire regardless.
    logger.warn('lists: discarding a list on the server failed', error);
  });
}

/**
 * Let go of the list here without deleting it there.
 *
 * Used when a stored code turns out to name nothing any more, and when the
 * customer puts the list aside but keeps the code.
 */
export function forgetList(): void {
  customerList.set(null);
  listExcluded.set(false);
  applyExclusion();
}

/**
 * Follow a list until it is done.
 *
 * Polled rather than pushed: matching a list of any size takes minutes, and a
 * socket for a progress bar somebody watches once is machinery this does not
 * need. Stops on its own when the list finishes, fails or goes away.
 */
export function watchList(onChange?: (list: CompanyList) => void): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const tick = async () => {
    if (stopped) return;

    try {
      const list = await refreshList();
      if (!list) return;

      onChange?.(list);
      if (list.status === 'ready' || list.status === 'failed') return;
    } catch {
      // A list that has gone stops being followed; forgetting it is the
      // customer's screen's decision, not the poller's.
      return;
    }

    timer = setTimeout(tick, pollInterval);
  };

  timer = setTimeout(tick, pollInterval);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

/** How often a list being matched is asked about. */
const pollInterval = 2000;

/**
 * Which search to re-run after the subtraction changes.
 *
 * A similarity selection is defined by its example companies, not by the
 * filters, so re-running it means asking for the neighbours again. Running the
 * classic search instead asks the app to search with no criteria at all, which
 * it refuses — and the customer, who had only ticked a box, was told "an
 * unexpected error occurred" and got no subtraction.
 *
 * 'none' is the honest answer where there is nothing on screen to re-run: on
 * the guide, where the count is what moves, and in similarity mode before any
 * example has been named.
 */
export function searchToRerun(
  mode: string,
  hasSamples: boolean,
  hasResults: boolean
): 'neighbor' | 'classic' | 'none' {
  if (mode === 'neighbor') return hasSamples ? 'neighbor' : 'none';
  return hasResults ? 'classic' : 'none';
}

/**
 * React to a search refused because the list it subtracts has gone.
 *
 * The service refuses rather than running the search unsubtracted, because a
 * selection that silently includes what the customer already owns is bought as
 * one that does not. Here that refusal is turned into the two things the
 * customer needs: the code let go of, so the next search runs, and a sentence
 * saying why.
 *
 * Returns whether it was that error, so a caller can stop treating it as a
 * failure of the search.
 */
export function listHasGone(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  if ((error.data as { error?: string } | undefined)?.error !== 'list_gone') return false;

  forgetList();
  return true;
}
